import React, { ReactNode, useState } from 'react';
import { Stack } from '@mui/material';
import { logError } from 'utils/logger';
import { isNullOrUndefined } from 'utils/typeGuargs';
import { useNotifications } from 'modules/notification';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';
import { Nullable, QueryListResponse, SelectOption } from 'global';
import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';

import { StageView } from './StageView';
import { BPFAPINameSpase, BpfCloseReasonType, StagesViewType } from '../../types';
import { ActionsProps, ManagerApiContainerProps, TemplatesState } from './types';
import {
  getCurrentStage,
  templatesDataNormalizer,
  isAllStagesForFlowCompleted,
  getCurrentActiveFlowAndDepartment,
} from '../../lib';
import {
  bpfAPI,
  Stage,
  StageId,
  DepartmentId,
  useBPFMutations,
  TemplatesResponse,
  UpdateBPFVariables,
  GetTemplatesVariables,
  UpdateBPFVariablesSchema,
  TemplatesAndStagesResponse,
} from '../../api';

export const StagesContainer = ({
  children,
  parentId,
  accountId,
  parentType,
  isDealClosed,
  closedStatus,
  closeReasonId,
}: ManagerApiContainerProps) => {
  const { showNotification } = useNotifications();

  const [genericLoading, setGenericLoading] = useState<boolean>(true);

  const [activeFlowId, setActiveFlowId] = useState<string | null>(null);

  const [stagesViewType, setStagesViewType] = useState<StagesViewType>('active');
  const [timelineView, setTimelineView] = useState<'on' | 'off'>('off');
  const [closeFlow, setCloseFlow] = useState<Nullable<BpfCloseReasonType>>(
    closedStatus ? (closedStatus === 'SUCCESS' ? BpfCloseReasonType.Won : BpfCloseReasonType.Lost) : null
  );

  const [activeStageId, setActiveStageId] = useState<Nullable<StageId>>(null);
  const [viewedStageId, setViewedStageId] = useState<Nullable<StageId>>(null);

  const handleSetActiveAndViewedStages = (id: Nullable<StageId>) => {
    setActiveStageId(id);
    setViewedStageId(id);
  };

  const [activeDepartmentId, setActiveDepartmentId] = useState<Nullable<DepartmentId>>(null);
  const [templatesData, setTemplatesData] = useState<TemplatesState>({});

  const [departmentFlowMap, setDepartmentFlowMap] = useState<Record<string, string>>({});

  const {
    isBPFsUpdating,
    isStagesUpdating,
    isStageCompleting,
    handleCompleteStage,
    onUpdateDealCurrentStage,
    handleUpdateStageAttributes,
  } = useBPFMutations();

  const [fetchTemplates, { loading: isTemplatesLoading }] = useLazyQuery<TemplatesResponse, GetTemplatesVariables>(
    bpfAPI.fetchTemplates
  );

  const { data: departments, loading: isDepartmentsLoading } = useQuery<
    BPFAPINameSpase<QueryListResponse<SelectOption>>
  >(bpfAPI.department.fetchDepartmentsOptions);

  const graphqlDocuments = {
    VO: bpfAPI.fetchVOTemplatesAndStages,
    CS: bpfAPI.fetchCSTemplatesAndStages,
  };

  const graphqlVariables = {
    VO: { id: parentId },
    CS: { dynamicsId: null, creditSubmissionId: parentId },
  };

  const {
    loading,
    networkStatus,
    refetch: fetchTemplatesAndStages,
    data: templatesAndStagesResponse,
  } = useQuery<TemplatesAndStagesResponse>(graphqlDocuments[parentType], {
    variables: graphqlVariables[parentType],
    notifyOnNetworkStatusChange: true,
    onCompleted({ result: { businessProcessFlows } }) {
      if (!businessProcessFlows) {
        setGenericLoading(false);
        return;
      }

      fetchTemplates({ variables: { input: { accountId, parentId, parentType }, parentId, parentType } })
        .then(({ data: templatesRes }) => {
          if (!templatesRes || !Array.isArray(templatesRes?.bpf?.templates)) {
            showNotification('Something went wrong', { type: 'error' });
            return;
          }

          const { templatesByFlowId, flowDepartmentMap } = templatesDataNormalizer(
            businessProcessFlows?.flows ?? [],
            templatesRes
          );

          setTemplatesData(templatesByFlowId);
          setDepartmentFlowMap(flowDepartmentMap);

          const { departmentId, flowId } = getCurrentActiveFlowAndDepartment(templatesByFlowId);

          if (!activeFlowId) setActiveFlowId(flowId);

          if (!activeDepartmentId) setActiveDepartmentId(departmentId);

          if (!activeStageId || !viewedStageId) {
            handleSetActiveAndViewedStages(getCurrentStage(flowId, businessProcessFlows?.flows ?? []));
          }

          if (isAllStagesForFlowCompleted(templatesByFlowId, flowId)) {
            const arrayOfFlowsIds = Object.keys(templatesByFlowId);

            const currentFlowIndex = arrayOfFlowsIds.findIndex(id => id === flowId);

            const newTemplateIndex = currentFlowIndex + 1;
            const idOfNextActiveTemplate = arrayOfFlowsIds[newTemplateIndex];

            if (!idOfNextActiveTemplate) return;

            const [idOfNextActiveStage] = Object.keys(templatesByFlowId[idOfNextActiveTemplate].stages);

            handleSetActiveAndViewedStages(idOfNextActiveStage);
          }
        })
        .catch(error => {
          logError(error);
          showNotification('Something went wrong', { type: 'error' });
        })
        .finally(() => {
          setGenericLoading(false);
        });
    },
  });

  const handleUpdateBusinessProcessFlows = async (flowId: string, nextStage: string) => {
    try {
      if (!templatesAndStagesResponse?.result?.businessProcessFlows?.flows?.length) return;

      const updateDto: UpdateBPFVariables['input'] = { parentId, parentType, nextStage, flowId };
      const { data: checkedVariables, success, error } = UpdateBPFVariablesSchema.safeParse({ input: updateDto });

      if (success) {
        await onUpdateDealCurrentStage({ variables: checkedVariables });
        await fetchTemplatesAndStages();

        handleSetActiveAndViewedStages(nextStage);
      } else {
        showNotification('Something went wrong', { type: 'error' });
        logError('zod parse error', error);
      }
    } catch (error) {
      logError(error);
      showNotification('Something went wrong', { type: 'error' });
    }
  };

  const handleViewStage = (stage: Stage) => {
    setViewedStageId(prev =>
      prev === stage.businessProcessFlowStageId ? activeStageId : stage.businessProcessFlowStageId
    );
  };

  const stagesOptions = Object.entries(templatesData?.[activeFlowId!]?.stages ?? {}).map(([stageId, stage]) => ({
    value: stageId,
    label: stage.name,
  }));

  const handleChangeDepartment = (departmentId: string) => {
    setActiveDepartmentId(departmentId);
    setActiveFlowId(departmentFlowMap[departmentId]);

    handleSetActiveAndViewedStages(
      getCurrentStage(
        departmentFlowMap[departmentId],
        templatesAndStagesResponse?.result?.businessProcessFlows?.flows ?? []
      )
    );
  };

  const handleCompleteStageAndMoveToNext = async (stageId: StageId, parentId: string, parentType: BpfLinkType) => {
    const isCompletedSuccess = await handleCompleteStage(stageId, parentId, parentType);

    if (!isCompletedSuccess) return;

    const currentStageIndex = stagesOptions.findIndex(({ value }) => value === stageId);

    const nextStage = stagesOptions?.[currentStageIndex + 1]?.value;

    if (nextStage && activeFlowId) {
      await handleUpdateBusinessProcessFlows(activeFlowId, nextStage);
    } else {
      const departmentsOptions = departments?.bpf?.result?.list ?? [];

      const activeDepartmentIndex = departmentsOptions.findIndex(option => option.value === activeDepartmentId);
      const nextDepartmentId = departmentsOptions?.[activeDepartmentIndex + 1]?.value;

      if (!nextDepartmentId) return;

      const nextStageId = Object.keys(templatesData[departmentFlowMap[nextDepartmentId]].stages)?.[0];

      if (!nextStageId) return;

      setActiveDepartmentId(nextDepartmentId);
      handleSetActiveAndViewedStages(
        getCurrentStage(
          departmentFlowMap[nextDepartmentId],
          templatesAndStagesResponse?.result?.businessProcessFlows?.flows ?? []
        )
      );
      await handleUpdateBusinessProcessFlows(departmentFlowMap[nextDepartmentId], nextStageId);
    }
  };

  const activeStage = activeFlowId && activeStageId ? templatesData[activeFlowId]?.stages[activeStageId] : null;
  const viewedStage = activeFlowId && viewedStageId ? templatesData[activeFlowId]?.stages[viewedStageId] : null;

  const actions: ActionsProps['actions'] = {
    onViewStage: handleViewStage,
    onChangeDepartment: handleChangeDepartment,
    onCompleteStage: handleCompleteStageAndMoveToNext,
    onUpdateStageAttributes: handleUpdateStageAttributes,
    onChangeCloseFlow: (value: Nullable<BpfCloseReasonType>) => setCloseFlow(value),
    onSwitchTimeline: () => setTimelineView(prevState => (prevState === 'on' ? 'off' : 'on')),
    onChangeViewType: () => setStagesViewType(prevState => (prevState === 'active' ? 'all' : 'active')),
  };

  const stageViewCommonProps = {
    actions,
    parentId,
    closeFlow,
    parentType,
    isDealClosed,
    closeReasonId,
    isBPFsUpdating,
    isStagesUpdating,
    isStageCompleting,
  };

  const renderStagesViewByType: Record<StagesViewType, ReactNode> = {
    active: (
      <StageView
        stage={viewedStage || activeStage}
        isCompleteDisabled={!activeStageId || activeStageId !== viewedStageId}
        isStageLastForDepartment={!!viewedStage?.isLastStage || !!activeStage?.isLastStage}
        {...stageViewCommonProps}
      />
    ),
    all: (
      <Stack gap="0.5rem">
        {Object.values(templatesData[activeFlowId!]?.stages ?? []).map(stage => (
          <StageView
            stage={stage}
            isStageLastForDepartment={stage.isLastStage}
            isCompleteDisabled={activeStageId !== stage.businessProcessFlowStageId}
            {...stageViewCommonProps}
          />
        ))}
      </Stack>
    ),
  };

  if (isNullOrUndefined(templatesAndStagesResponse?.result?.businessProcessFlows) && !loading) {
    logError(`For ${parentType} with id ${parentId} BPF data empty. Nothing to render, pls check settings!`);
    return null;
  }

  return (
    <>
      {children({
        actions,
        closeFlow,
        timelineView,
        stagesViewType,
        activeDepartmentId,
        templates: templatesData,
        activeTemplate: activeFlowId,
        stagesView: renderStagesViewByType[stagesViewType],
        departmentsOptions: departments?.bpf?.result?.list ?? [],
        stages: { activeStage, viewedStage, viewedStageId, activeStageId, stagesOptions },
        loading: {
          genericLoading,
          isStagesUpdating,
          isStageCompleting,
          isTemplatesLoading,
          isDepartmentsLoading,
          isStagesLoading: loading || isTemplatesLoading,
          isBPFsUpdating: isBPFsUpdating || networkStatus === NetworkStatus.refetch,
        },
      })}
    </>
  );
};
